import { effect, inject } from '@angular/core';
import { BaseEnumResponse, LocalizedValue } from '@app/models/shared.models';
import { trackUnauthorizedLoad } from '@app/helpers/master-data-unauthorized-load.helper';
import { GenericDataService } from '@app/services/generic-data.service';
import { AuthStore } from '@app/stores/auth.store';
import {
  patchState,
  signalStore,
  signalStoreFeature,
  type,
  withHooks,
} from '@ngrx/signals';
import {
  entityConfig,
  setEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { forkJoin, Observable } from 'rxjs';

const projectStatusConfig = entityConfig({
  entity: type<BaseEnumResponse>(),
  collection: 'projectStatus',
  selectId: (entity) => entity.id,
});
const projectFoundingConfig = entityConfig({
  entity: type<BaseEnumResponse>(),
  collection: 'projectFounding',
  selectId: (entity) => entity.id,
});
const financialAssetProjectConnectionConfig = entityConfig({
  entity: type<BaseEnumResponse>(),
  collection: 'financialAssetProjectConnection',
  selectId: (entity) => entity.id,
});

const withProjectClassificationAndMeetingStatus = signalStoreFeature(
  withEntities({
    entity: type<LocalizedValue>(),
    collection: 'projectClassification',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'projectMeetingStatus',
  }),
);

type ProjectLoadKey =
  | 'projectStatus'
  | 'projectFounding'
  | 'financialAssetProjectConnection'
  | 'projectLoanCoverage'
  | 'projectLoanUsage'
  | 'projectLoanTerm'
  | 'projectLoanInterestRatesType'
  | 'projectLoanInterestPaymentType'
  | 'loanTypePaymentFrequency'
  | 'legalEntityInvolvement'
  | 'overSubscriptionType'
  | 'securitiesType'
  | 'financialAssetConnectionTypeToProject'
  | 'projectClassification'
  | 'projectMeetingStatus';

const allProjectLoadKeys: ProjectLoadKey[] = [
  'projectStatus',
  'projectFounding',
  'financialAssetProjectConnection',
  'projectLoanCoverage',
  'projectLoanUsage',
  'projectLoanTerm',
  'projectLoanInterestRatesType',
  'projectLoanInterestPaymentType',
  'loanTypePaymentFrequency',
  'legalEntityInvolvement',
  'overSubscriptionType',
  'securitiesType',
  'financialAssetConnectionTypeToProject',
  'projectClassification',
  'projectMeetingStatus',
];

export const GenericProjectStore = signalStore(
  { providedIn: 'root' },
  withEntities(projectStatusConfig),
  withEntities(projectFoundingConfig),
  withEntities(financialAssetProjectConnectionConfig),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'projectLoanCoverage',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'projectLoanUsage',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'projectLoanTerm',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'projectLoanInterestRatesType',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'projectLoanInterestPaymentType',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'loanTypePaymentFrequency',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'legalEntityInvolvement',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'overSubscriptionType',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'securitiesType',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'financialAssetConnectionTypeToProject',
  }),
  withProjectClassificationAndMeetingStatus,
  withHooks((store) => {
    const genericDataService = inject(GenericDataService);
    const authStore = inject(AuthStore);
    const pendingUnauthorizedLoads = new Set<ProjectLoadKey>();
    const track = <T>(key: ProjectLoadKey, empty: T) =>
      trackUnauthorizedLoad(key, pendingUnauthorizedLoads, empty);

    const buildLoadRequests = (keys: Iterable<ProjectLoadKey>) => {
      const requests: Partial<Record<ProjectLoadKey, Observable<unknown>>> =
        {};

      for (const key of keys) {
        switch (key) {
          case 'projectStatus':
            requests.projectStatus = genericDataService
              .loadProjectStatus()
              .pipe(track('projectStatus', [] as BaseEnumResponse[]));
            break;
          case 'projectFounding':
            requests.projectFounding = genericDataService
              .loadProjectFounding()
              .pipe(track('projectFounding', [] as BaseEnumResponse[]));
            break;
          case 'financialAssetProjectConnection':
            requests.financialAssetProjectConnection = genericDataService
              .loadFinancialAssetProjectConnection()
              .pipe(
                track(
                  'financialAssetProjectConnection',
                  [] as BaseEnumResponse[],
                ),
              );
            break;
          case 'projectLoanCoverage':
            requests.projectLoanCoverage = genericDataService
              .loadProjectLoanCoverage()
              .pipe(track('projectLoanCoverage', [] as BaseEnumResponse[]));
            break;
          case 'projectLoanUsage':
            requests.projectLoanUsage = genericDataService
              .loadProjectLoanUsage()
              .pipe(track('projectLoanUsage', [] as BaseEnumResponse[]));
            break;
          case 'projectLoanTerm':
            requests.projectLoanTerm = genericDataService
              .loadProjectLoanTerm()
              .pipe(track('projectLoanTerm', [] as BaseEnumResponse[]));
            break;
          case 'projectLoanInterestRatesType':
            requests.projectLoanInterestRatesType = genericDataService
              .loadProjectLoanInterestRatesType()
              .pipe(
                track(
                  'projectLoanInterestRatesType',
                  [] as BaseEnumResponse[],
                ),
              );
            break;
          case 'projectLoanInterestPaymentType':
            requests.projectLoanInterestPaymentType = genericDataService
              .loadProjectLoanInterestPaymentType()
              .pipe(
                track(
                  'projectLoanInterestPaymentType',
                  [] as BaseEnumResponse[],
                ),
              );
            break;
          case 'loanTypePaymentFrequency':
            requests.loanTypePaymentFrequency = genericDataService
              .loadLoanTypePaymentFrequency()
              .pipe(
                track('loanTypePaymentFrequency', [] as BaseEnumResponse[]),
              );
            break;
          case 'legalEntityInvolvement':
            requests.legalEntityInvolvement = genericDataService
              .loadLegalEntityInvolvement()
              .pipe(track('legalEntityInvolvement', [] as BaseEnumResponse[]));
            break;
          case 'overSubscriptionType':
            requests.overSubscriptionType = genericDataService
              .loadOversubscriptionType()
              .pipe(track('overSubscriptionType', [] as BaseEnumResponse[]));
            break;
          case 'securitiesType':
            requests.securitiesType = genericDataService
              .loadSecuritiesType()
              .pipe(track('securitiesType', [] as BaseEnumResponse[]));
            break;
          case 'financialAssetConnectionTypeToProject':
            requests.financialAssetConnectionTypeToProject =
              genericDataService
                .loadFinancialAssetConnectionTypeToProject()
                .pipe(
                  track(
                    'financialAssetConnectionTypeToProject',
                    [] as BaseEnumResponse[],
                  ),
                );
            break;
          case 'projectClassification':
            requests.projectClassification = genericDataService
              .projectClassifications()
              .pipe(track('projectClassification', [] as LocalizedValue[]));
            break;
          case 'projectMeetingStatus':
            requests.projectMeetingStatus = genericDataService
              .loadProjectMeetingStatus()
              .pipe(track('projectMeetingStatus', [] as BaseEnumResponse[]));
            break;
        }
      }

      return requests;
    };

    const applyLoadResults = (
      results: Partial<Record<ProjectLoadKey, unknown>>,
    ): void => {
      const patches = [];

      if (results.projectStatus) {
        patches.push(
          setEntities(
            results.projectStatus as BaseEnumResponse[],
            projectStatusConfig,
          ),
        );
      }
      if (results.projectFounding) {
        patches.push(
          setEntities(
            results.projectFounding as BaseEnumResponse[],
            projectFoundingConfig,
          ),
        );
      }
      if (results.financialAssetProjectConnection) {
        patches.push(
          setEntities(
            results.financialAssetProjectConnection as BaseEnumResponse[],
            financialAssetProjectConnectionConfig,
          ),
        );
      }
      if (results.projectLoanCoverage) {
        patches.push(
          setEntities(results.projectLoanCoverage as BaseEnumResponse[], {
            collection: 'projectLoanCoverage',
          }),
        );
      }
      if (results.projectLoanUsage) {
        patches.push(
          setEntities(results.projectLoanUsage as BaseEnumResponse[], {
            collection: 'projectLoanUsage',
          }),
        );
      }
      if (results.projectLoanTerm) {
        patches.push(
          setEntities(results.projectLoanTerm as BaseEnumResponse[], {
            collection: 'projectLoanTerm',
          }),
        );
      }
      if (results.projectLoanInterestRatesType) {
        patches.push(
          setEntities(
            results.projectLoanInterestRatesType as BaseEnumResponse[],
            { collection: 'projectLoanInterestRatesType' },
          ),
        );
      }
      if (results.projectLoanInterestPaymentType) {
        patches.push(
          setEntities(
            results.projectLoanInterestPaymentType as BaseEnumResponse[],
            { collection: 'projectLoanInterestPaymentType' },
          ),
        );
      }
      if (results.loanTypePaymentFrequency) {
        patches.push(
          setEntities(results.loanTypePaymentFrequency as BaseEnumResponse[], {
            collection: 'loanTypePaymentFrequency',
          }),
        );
      }
      if (results.legalEntityInvolvement) {
        patches.push(
          setEntities(results.legalEntityInvolvement as BaseEnumResponse[], {
            collection: 'legalEntityInvolvement',
          }),
        );
      }
      if (results.overSubscriptionType) {
        patches.push(
          setEntities(results.overSubscriptionType as BaseEnumResponse[], {
            collection: 'overSubscriptionType',
          }),
        );
      }
      if (results.securitiesType) {
        patches.push(
          setEntities(results.securitiesType as BaseEnumResponse[], {
            collection: 'securitiesType',
          }),
        );
      }
      if (results.financialAssetConnectionTypeToProject) {
        patches.push(
          setEntities(
            results.financialAssetConnectionTypeToProject as BaseEnumResponse[],
            { collection: 'financialAssetConnectionTypeToProject' },
          ),
        );
      }
      if (results.projectClassification) {
        patches.push(
          setEntities(results.projectClassification as LocalizedValue[], {
            collection: 'projectClassification',
          }),
        );
      }
      if (results.projectMeetingStatus) {
        patches.push(
          setEntities(results.projectMeetingStatus as BaseEnumResponse[], {
            collection: 'projectMeetingStatus',
          }),
        );
      }

      if (patches.length > 0) {
        patchState(store, ...patches);
      }
    };

    const loadAll = (): void => {
      forkJoin(buildLoadRequests(allProjectLoadKeys)).subscribe((results) => {
        applyLoadResults(results);
      });
    };

    const reloadUnauthorizedOnly = (): void => {
      if (pendingUnauthorizedLoads.size === 0) {
        return;
      }

      const keys = [...pendingUnauthorizedLoads];
      forkJoin(buildLoadRequests(keys)).subscribe((results) => {
        applyLoadResults(results);
      });
    };

    return {
      onInit() {
        loadAll();

        let wasLoggedIn = authStore.isLoggedIn();
        effect(() => {
          const loggedIn = authStore.isLoggedIn();
          if (loggedIn && !wasLoggedIn) {
            reloadUnauthorizedOnly();
          }
          wasLoggedIn = loggedIn;
        });
      },
    };
  }),
);
